import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import Alert from '../components/Alert';
import Spinner from '../components/Spinner';
import AIAssistant from '../components/AIAssistant';
import BCPPageTitle from '../components/BCPPageTitle';
import GuideTopAI from '../components/GuideTopAI';
import { navigationService } from '../utils/navigation';
import { exportBCP } from '../services/api';
import { useLoadingState } from '../hooks/useLoadingState';
import { useIntroBCPGeneration } from '../hooks/useIntroBCPGeneration';
import { useAutoDismissMessage } from '../hooks/useAutoDismissMessage';
import { useBCPLoader } from '../hooks/useBCPLoader';
import { useBeforeUnload } from '../hooks/useBeforeUnload';
import { bcpService } from '../services/bcpService';
import type { BCPSaveRequest, BCPUpdateRequest } from '../types/bcp';
import {
  BCPFormContainer,
  BCPFormLayout,
  BCPSectionCard,
  ReferenceMaterialsPanel,
} from '../components/BCP';
import { INTRODUCTORY_BCP_SECTIONS } from '../config/introductoryBcpSections';
import { useBCPDynamicForm } from '../hooks/useBCPDynamicForm';
import { useAIConsultContext } from '../hooks/useAIConsultContext';
import BCPDownloadModal from '../components/BCPDownloadModal';

interface NavigationState {
  uploadedFiles: any[];
  apiResponse?: any;
  companyInfo?: {
    companyName: string;
    location: string;
    employeeCount?: string;
    businessDescription: string;
    criticalBusiness: string;
  };
}

const INTRODUCTORY_FIELD_IDS = INTRODUCTORY_BCP_SECTIONS.flatMap((section) =>
  section.fields.map((field) => field.id)
);

const extractIntroductoryFormValues = (source?: Record<string, any>): Record<string, string> => {
  if (!source) {
    return {};
  }

  const result: Record<string, string> = {};

  INTRODUCTORY_FIELD_IDS.forEach((fieldId) => {
    const value = source[fieldId];
    if (typeof value === 'string' && value.trim().length > 0) {
      result[fieldId] = value;
    }
  });

  return result;
};

const IntroductoryBCPCreatePage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const exportState = useLoadingState<Blob>();
  const [generalErrors, setGeneralErrors] = useState<string[]>([]);
  const [saveLoading, setSaveLoading] = useState(false);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(2);
  const [savedBcpId, setSavedBcpId] = useState<string | null>(null);
  const { message: successMessage, showMessage: setSuccessMessage } = useAutoDismissMessage();

  // 離脱防止フラグ
  // - isSavingOrDownloading: 保存・ダウンロード処理中
  // - hasSaved: 保存成功後 (TOPへ戻る際は確認不要)
  const [isSavingOrDownloading, setIsSavingOrDownloading] = useState(false);
  const [hasSaved, setHasSaved] = useState(false);

  const bcpIdFromUrl = searchParams.get('bcpId');
  const tenantIdFromUrl = searchParams.get('tenantId');
  const openModalParam = searchParams.get('openModal');

  const state = navigationService.extractLocationState<NavigationState>(location, {
    uploadedFiles: [],
    apiResponse: undefined,
  });
  const { uploadedFiles, apiResponse, companyInfo } = state;

  const {
    isLoading: generationLoading,
    formData: generatedFormData,
    hasGenerated,
    error: generationError,
  } = useIntroBCPGeneration({
    uploadedFiles,
    apiResponse,
    isEditMode: !!bcpIdFromUrl,
    companyInfo,
  });

  const {
    loading: bcpLoading,
    isEditMode,
    bcpDocument,
    getInitialFormData,
  } = useBCPLoader({
    bcpId: bcpIdFromUrl,
    tenantId: tenantIdFromUrl,
    onError: (error) => {
      setGeneralErrors([error]);
    },
  });

  const form = useBCPDynamicForm({
    sections: INTRODUCTORY_BCP_SECTIONS,
  });

  const { values, errors: fieldErrors, setFieldValue, reset, validate } = form;

  const aiConsultContext = useAIConsultContext({
    sections: INTRODUCTORY_BCP_SECTIONS,
    values,
    bcpType: 'simple',
    onFieldValueChange: setFieldValue,
  });

  // 離脱防止を有効化
  // 保存成功後またはダウンロード処理中は確認をスキップ
  useBeforeUnload({
    enabled: true,
    shouldBypass: () => isSavingOrDownloading || hasSaved,
  });

  useEffect(() => {
    if (hasGenerated && !isEditMode && Object.keys(generatedFormData).length > 0) {
      reset(generatedFormData);
      setSuccessMessage('AIによるBCP下書きを生成しました。');
      setGeneralErrors([]);
    }
  }, [hasGenerated, isEditMode, generatedFormData, reset, setSuccessMessage]);

  useEffect(() => {
    if (isEditMode && bcpDocument) {
      const initialData = extractIntroductoryFormValues(getInitialFormData() as Record<string, any>);
      reset(initialData);
      setSuccessMessage('BCPを編集モードで読み込みました。');
      setGeneralErrors([]);
    }
  }, [isEditMode, bcpDocument, getInitialFormData, reset, setSuccessMessage]);

  useEffect(() => {
    if (generationError) {
      setGeneralErrors([generationError]);
    }
  }, [generationError]);

  // Effect to open modal when redirected after save (only for edit mode with openModal param)
  useEffect(() => {
    console.log('[IntroductoryBCPCreatePage] Modal effect triggered');
    console.log('[IntroductoryBCPCreatePage] openModalParam:', openModalParam);
    console.log('[IntroductoryBCPCreatePage] bcpIdFromUrl:', bcpIdFromUrl);

    if (openModalParam === 'true' && bcpIdFromUrl && isEditMode) {
      console.log('[IntroductoryBCPCreatePage] Opening modal');
      setCurrentStep(3);
      setIsDownloadModalOpen(true);

      // Remove openModal param from URL
      const params = new URLSearchParams(window.location.search);
      params.delete('openModal');
      const newSearch = params.toString();
      const newUrl = newSearch
        ? `/introductory-bcp-create?${newSearch}`
        : '/introductory-bcp-create';
      window.history.replaceState({}, '', newUrl);
    }
  }, [openModalParam, bcpIdFromUrl, isEditMode]);

  const handleFieldChange = useCallback(
    (fieldId: string, value: string) => {
      setFieldValue(fieldId, value);
      if (generalErrors.length > 0) {
        setGeneralErrors([]);
      }
    },
    [setFieldValue, generalErrors.length]
  );

  const handleSave = async () => {
    // 保存中または保存済みの場合はスキップ
    if (isSavingOrDownloading || hasSaved) {
      return;
    }

    const isValid = validate();
    if (!isValid) {
      setGeneralErrors(['入力内容に不備があります。各項目を確認してください。']);
      return;
    }

    setSaveLoading(true);
    setIsSavingOrDownloading(true);
    try {
      if (isEditMode && bcpDocument) {
        const updateRequest: BCPUpdateRequest = {
          content: {
            title:
              bcpDocument.content.title || `超入門版BCP_${new Date().toLocaleDateString('ja-JP')}`,
            sections: values,
          },
          form_data: values,
          status: 'draft',
          tags: bcpDocument.tags || ['generated', 'introductory'],
        };

        const updatedBCP = await bcpService.updateBCP(bcpDocument.id, updateRequest);
        setSavedBcpId(updatedBCP.id);
        setHasSaved(true); // 保存成功後は離脱確認不要
        setSuccessMessage(`BCPが正常に更新されました。(ID: ${updatedBCP.id})`);
      } else {
        const saveRequest: BCPSaveRequest = {
          type: 'introductory',
          content: {
            title: `超入門版BCP_${new Date().toLocaleDateString('ja-JP')}`,
            sections: values,
          },
          form_data: values,
          generation_metadata: apiResponse?.generation_metadata,
          status: 'published',
          category: 'generated',
          tags: ['generated', 'introductory'],
        };

        const savedBCP = await bcpService.saveBCP(saveRequest);
        const newBcpId = savedBCP.id;
        setSavedBcpId(newBcpId);
        setHasSaved(true); // 保存成功後は離脱確認不要
        setSuccessMessage(`保存が完了しました。`);

        // Navigate to edit mode URL (replace history to prevent regeneration on back/forward)
        const params = new URLSearchParams();
        params.set('bcpId', newBcpId);
        if (tenantIdFromUrl) {
          params.set('tenantId', tenantIdFromUrl);
        }
        navigate(`/create-bcp-intro?${params.toString()}`, { replace: true });
      }
      setGeneralErrors([]);
    } catch (error) {
      console.error('BCP保存エラー:', error);
      setGeneralErrors(['BCPの保存に失敗しました。']);
    } finally {
      setSaveLoading(false);
      setIsSavingOrDownloading(false);
    }
  };

  const handleExport = async (format: 'pdf' | 'docx') => {
    await exportState.execute(async () => {
      const response = await exportBCP(values, format);

      if (response.success) {
        setSuccessMessage(`BCP ${format.toUpperCase()}ファイルのダウンロードを開始しました。`);
        return new Blob();
      }
      throw new Error('Export failed');
    });
  };

  const handleExportPdf = async () => {
    // 保存中の場合はスキップ
    if (isSavingOrDownloading) {
      return;
    }

    // BCPが保存されていない場合は先に保存する
    if (!bcpIdFromUrl && !savedBcpId) {
      // バリデーションチェック (フラグ設定前に実施)
      const isValid = validate();
      if (!isValid) {
        setGeneralErrors(['入力内容に不備があります。各項目を確認してください。']);
        return;
      }

      setSaveLoading(true);
      setIsSavingOrDownloading(true);
      try {
        if (isEditMode && bcpDocument) {
          const updateRequest: BCPUpdateRequest = {
            content: {
              title:
                bcpDocument.content.title || `超入門版BCP_${new Date().toLocaleDateString('ja-JP')}`,
              sections: values,
            },
            form_data: values,
            status: 'published',
            tags: bcpDocument.tags || ['generated', 'introductory'],
          };

          const updatedBCP = await bcpService.updateBCP(bcpDocument.id, updateRequest);
          setSavedBcpId(updatedBCP.id);
          setHasSaved(true); // 保存成功後は離脱確認不要
          setSuccessMessage(`BCPが正常に更新されました。(ID: ${updatedBCP.id})`);
        } else {
          const saveRequest: BCPSaveRequest = {
            type: 'introductory',
            content: {
              title: `超入門版BCP_${new Date().toLocaleDateString('ja-JP')}`,
              sections: values,
            },
            form_data: values,
            generation_metadata: apiResponse?.generation_metadata,
            status: 'published',
            category: 'generated',
            tags: ['generated', 'introductory'],
          };

          const savedBCP = await bcpService.saveBCP(saveRequest);
          const newBcpId = savedBCP.id;
          setSavedBcpId(newBcpId);
          setHasSaved(true); // 保存成功後は離脱確認不要
          setSuccessMessage(`保存が完了しました。`);

          // Navigate to edit mode URL (replace history to prevent regeneration on back/forward)
          const params = new URLSearchParams();
          params.set('bcpId', newBcpId);
          if (tenantIdFromUrl) {
            params.set('tenantId', tenantIdFromUrl);
          }
          navigate(`/create-bcp-intro?${params.toString()}`, { replace: true });
        }
        setGeneralErrors([]);
        // Wait a bit for state to update
        await new Promise(resolve => setTimeout(resolve, 100));
      } catch (error) {
        console.error('BCP保存エラー:', error);
        setGeneralErrors(['BCPの保存に失敗しました。']);
        return;
      } finally {
        setSaveLoading(false);
        setIsSavingOrDownloading(false);
      }
    }
    // 保存後または既に保存済みの場合はモーダルを開く
    setCurrentStep(3);
    setIsDownloadModalOpen(true);
  };

  const aggregatedFieldErrors = useMemo(() => Object.values(fieldErrors), [fieldErrors]);

  if (generationLoading || bcpLoading) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="text-center py-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full mb-4">
            <Spinner size="lg" />
          </div>
          <h2 className="text-xl font-medium text-gray-900 mb-2">AIがBCPを生成中...</h2>
          <p className="text-gray-600">
            アップロードされた資料を分析して、貴社の事業内容に応じたBCPの下書きを作成しています。
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <BCPFormLayout
      currentStep={currentStep}
      showHeaderActions
      isEditMode={isEditMode}
      onSave={handleSave}
      onDownload={handleExportPdf}
      saveLoading={saveLoading || isSavingOrDownloading}
      downloadLoading={exportState.isLoading || isSavingOrDownloading}
      saveDisabled={isSavingOrDownloading || hasSaved}
      downloadDisabled={isSavingOrDownloading}
      fieldContext={aiConsultContext.fieldContext}
      onAIContentApply={aiConsultContext.handleAIContentApply}
      bcpType={aiConsultContext.bcpType}
    >
      <BCPFormContainer
        header={
          <>
            <BCPPageTitle
              title="超入門版BCPシート"
              isEditMode={isEditMode}
              editInfo={
                bcpDocument
                  ? {
                      title: bcpDocument.content.title,
                      id: bcpDocument.id,
                    }
                  : undefined
              }
            />
            <div className="mt-6 flex justify-center">
              <GuideTopAI />
            </div>
          </>
        }
        alerts={
          <div className="space-y-4">
            {successMessage && (
              <Alert type="success" message={successMessage} />
            )}
            {generalErrors.length > 0 && (
              <Alert
                type="error"
                title="エラーが発生しました"
                messages={generalErrors}
              />
            )}
            {aggregatedFieldErrors.length > 0 && (
              <Alert
                type="error"
                title="入力内容を確認してください"
                messages={aggregatedFieldErrors}
              />
            )}
          </div>
        }
        footer={
          <ReferenceMaterialsPanel uploadedFiles={uploadedFiles} className="mt-6" />
        }
      >
        {INTRODUCTORY_BCP_SECTIONS.map((section) => (
          <BCPSectionCard
            key={section.id}
            section={section}
            values={values}
            errors={fieldErrors}
            onChange={handleFieldChange}
            onAIConsult={aiConsultContext.handleAIConsult}
          />
        ))}

        {/* 次へボタン */}
        <div className="mt-8 flex justify-center">
          <button
            onClick={handleExportPdf}
            disabled={isSavingOrDownloading}
            style={{
              backgroundColor: isSavingOrDownloading ? '#CCCCCC' : '#E60012',
              color: 'white',
              padding: '12px 32px',
              borderRadius: '8px',
              border: 'none',
              fontSize: '20px',
              fontWeight: 500,
              fontFamily: 'Noto Sans JP, sans-serif',
              lineHeight: '20px',
              cursor: isSavingOrDownloading ? 'not-allowed' : 'pointer',
              boxShadow: '0px 10px 15px -3px rgba(0,0,0,0.1), 0px 4px 6px -4px rgba(0,0,0,0.1)',
            }}
          >
            完成・ダウンロード
          </button>
        </div>
      </BCPFormContainer>
    </BCPFormLayout>

    {/* Download Modal */}
    <BCPDownloadModal
      isOpen={isDownloadModalOpen}
      onClose={() => {
        setIsDownloadModalOpen(false);
        setCurrentStep(2);
      }}
      bcpType="introductory"
      bcpId={bcpIdFromUrl || savedBcpId}
    />
  </>
  );
};

export default IntroductoryBCPCreatePage;
